import React, { createContext, useContext, useState, useEffect } from "react";
import { getLoggedInUser } from "../../apis/UserApis";
import { STORAGE_KEYS } from "../../config/storageKeys";

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Load from localStorage on first render
    const saved = localStorage.getItem(STORAGE_KEYS.USER);
    return saved ? JSON.parse(saved) : null;
  });

  const [loggedInUser, setLoggedInUser] = useState(user || null);

  useEffect(() => {
    let isMounted = true;

    // Keep derived user details in sync with auth state
    if (!user?.token) {
      setLoggedInUser(null);
      return () => {
        isMounted = false;
      };
    }

    // Optimistic: ensure UI updates immediately after login with token payload
    setLoggedInUser((prev) => prev || user);

    // Fetch canonical user details from API (role, profile fields, etc.)
    getLoggedInUser(user.token)
      .then((data) => {
        if (!isMounted) return;
        if (data) setLoggedInUser(data);
      })
      .catch((err) => console.error("Error fetching logged in user:", err));

    return () => {
      isMounted = false;
    };
  }, [user?.token]);

  const login = (userData) => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    setUser(userData);
    // Update immediately so role-based UI (admin) doesn't require refresh
    setLoggedInUser(userData || null);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.USER);
    setUser(null);
    setLoggedInUser(null);
  };

  const addUserDetails = (userDetails) => {
    const mergedUser = { ...user, ...userDetails };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(mergedUser));
    setUser(mergedUser);
    setLoggedInUser((prev) => ({ ...(prev || {}), ...userDetails }));
  };

  const isAuthenticated = Boolean(user?.token);

  return (
    <AuthContext.Provider
      value={{
        user,
        loggedInUser,
        setLoggedInUser,
        isAuthenticated,
        login,
        logout,
        addUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
